// externals
import type { FC, MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { paymentActionCreators } from '@makemydeal/dr-offer-redux';

// components
import { Typography } from '@interstate/components/Typography';
import { ManualLegendMessage } from '../taxes/Taxes/ManuakLegendMessage';
import { FeeResetDialog } from './FeeResetDialog';
import FeesFormCategoryItems from './FeesFormCategoryItems';
import type { FeesFormContextProps } from './FeesFormContext';
import { FeesFormContext } from './FeesFormContext';

// styles
import { TotalSummaryContainer } from './FeesForm.style';

// types/constants/enums
import type { FeeOverride } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { DealTabs, FeesCategoriesType } from '@makemydeal/dr-shared-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { FEES } from '../../constants';
import { ADDED_FEE_CODE, ELIMINATED_FEE_AMOUNT, SOFT_SAVE_DEBOUNCE_MS } from './constants';
import type { CustomFeeItem } from './types';

// utils
import {
    applyFeeOverrides,
    getAreFeesValid,
    getDefaultFeeRowErrorState,
    hasChangeDealerFees,
    hasChangeGovFees,
    hasChangeLenderFees,
    mergeFeeUpdates
} from './utils';
import { useFeeErrorSync } from './useFeeErrorSync';
import { ArrowUturnLeftIcon } from '@interstate/components/Icons/ArrowUturnLeftIcon';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { createNewFee } from '../../utils/components';
import { useBaseDealBreakpoint } from '../../utils/useBaseDealBreakpoint';

type FeesFormProps = {
    offerType: string;
    dealerFeesItems: CustomFeeItem[];
    lenderFeesItems: CustomFeeItem[];
    governmentFeesItems: CustomFeeItem[];
    onReset?: () => void;
    onSave?: (updatedFees: FeeOverride[], deletedFees: CustomFeeItem[], payment?: boolean) => void;
    getCappedFeesStatus?: () => boolean;
    isCapAllFeeClickedManually?: boolean;
    shouldTriggerValidation?: boolean;
    isFeesDirtyRef?: MutableRefObject<boolean>;
};

export const FeesForm: FC<FeesFormProps> = ({
    offerType,
    governmentFeesItems,
    dealerFeesItems,
    lenderFeesItems,
    onReset,
    onSave,
    getCappedFeesStatus = () => false,
    isCapAllFeeClickedManually = false,
    shouldTriggerValidation = false,
    isFeesDirtyRef
}) => {
    const dispatch = useDispatch();
    const totalFees = useSelector(offerReduxSelectors.getTotalFee);
    const feesOverrides = useSelector(offerReduxSelectors.getFeesOverride);
    const isAllFeesCapitalized = getCappedFeesStatus();
    const acqFeeUpFrontOverride = useSelector(offerSelectors.getAcqFeeUpFrontOverrideWithFallback);
    const acqFeeOverride = useSelector(offerSelectors.getAcqFeeOverrideWithFallback);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const isLocked = dealLockHooks.useIsDealLocked();

    const initialFeesRef = useRef<Record<keyof typeof setStatesByCategoryLookup, CustomFeeItem[]>>({
        Government: [],
        Dealer: [],
        Lender: []
    });
    const [govFees, setGovFees] = useState(governmentFeesItems);
    const [dealerFees, setDealerFees] = useState(dealerFeesItems);
    const [lenderFees, setLenderFees] = useState(lenderFeesItems);
    const [disableFeeSubmit, setDisableFeeSubmit] = useState(false);

    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const shouldCommitFeeSoftSave = useRef(false);

    const BaseBreakPoint = useBaseDealBreakpoint();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const governmentFeesErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.FEES, FeesCategoriesType.GOVERNMENT)
    );
    const dealerFeesErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.FEES, FeesCategoriesType.DEALER)
    );
    const lenderFeesErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.FEES, FeesCategoriesType.LENDER)
    );
    const getCurrentFeesErrors = useMemo(
        () => ({
            Government: governmentFeesErrors,
            Dealer: dealerFeesErrors,
            Lender: lenderFeesErrors
        }),
        [governmentFeesErrors, dealerFeesErrors, lenderFeesErrors]
    );

    const hasRunInitialCapRef = useRef(false);

    useEffect(() => {
        setLenderFees(lenderFeesItems);
    }, [acqFeeUpFrontOverride, acqFeeOverride]);

    useEffect(() => {
        setGovFees(isOptionalDeskingMode ? applyFeeOverrides(governmentFeesItems, feesOverrides) : governmentFeesItems);
    }, [governmentFeesItems]);

    useEffect(() => {
        setDealerFees(isOptionalDeskingMode ? applyFeeOverrides(dealerFeesItems, feesOverrides) : dealerFeesItems);
    }, [dealerFeesItems]);

    useEffect(() => {
        setLenderFees(isOptionalDeskingMode ? applyFeeOverrides(lenderFeesItems, feesOverrides) : lenderFeesItems);
    }, [lenderFeesItems]);

    const hasFeesChanged =
        hasChangeGovFees({
            initialGovFees: isOptionalDeskingMode ? applyFeeOverrides(governmentFeesItems, feesOverrides) : governmentFeesItems,
            updatedGovFees: govFees
        }) ||
        hasChangeDealerFees({
            initialDealerFees: isOptionalDeskingMode ? applyFeeOverrides(dealerFeesItems, feesOverrides) : dealerFeesItems,
            updatedDealerFees: dealerFees
        }) ||
        hasChangeLenderFees({
            initialLenderFees: isOptionalDeskingMode ? applyFeeOverrides(lenderFeesItems, feesOverrides) : lenderFeesItems,
            updatedLenderFees: lenderFees
        });

    const areFeesValid = getAreFeesValid(dealerFees, govFees, lenderFees, !isOptionalDeskingMode);
    const isLease = offerType === LEASE;

    // Update parent's dirty ref when the validation state changes
    useEffect(() => {
        if (isFeesDirtyRef) {
            isFeesDirtyRef.current = !areFeesValid;
        }
    }, [hasFeesChanged, areFeesValid]);

    const setStatesByCategoryLookup = useMemo(
        () => ({
            Government: setGovFees,
            Dealer: setDealerFees,
            Lender: setLenderFees
        }),
        [setGovFees, setDealerFees, setLenderFees]
    );
    useFeeErrorSync(FeesCategoriesType.GOVERNMENT, governmentFeesItems, governmentFeesErrors, isDealScreenRedDotErrorsEnabled);
    useFeeErrorSync(FeesCategoriesType.DEALER, dealerFeesItems, dealerFeesErrors, isDealScreenRedDotErrorsEnabled);
    useFeeErrorSync(FeesCategoriesType.LENDER, lenderFeesItems, lenderFeesErrors, isDealScreenRedDotErrorsEnabled);

    const dispatchRedDotFeeError = (category: string, errorState: Array<Record<string, boolean>>, index?: number) => {
        dispatch(dealScreenErrorActionCreators.setErrorFieldsByCategory(DealTabs.FEES, category, errorState, false, index));
    };

    const handleDelete: FeesFormContextProps['handleDelete'] = useCallback(
        ({ category, code, isDelete, index }) => {
            const setStateFunc = setStatesByCategoryLookup[category];

            if (!setStateFunc) return;

            if (!code) {
                setStateFunc((fees) => fees.filter((fee, ind) => ind !== index));
                if (isDealScreenRedDotErrorsEnabled) {
                    const updatedErrorState = { ...getCurrentFeesErrors };
                    updatedErrorState[category] = updatedErrorState[category].filter((_, rowIndex) => rowIndex !== index);

                    dispatchRedDotFeeError(category, updatedErrorState[category], index);
                }
                return;
            }

            setStateFunc((fees) =>
                fees.map((fee, ind) =>
                    fee.feeTypeId === code && ind === index
                        ? {
                              ...fee,
                              isDeleted: isDelete
                          }
                        : fee
                )
            );
            if (isDealScreenRedDotErrorsEnabled) {
                const updatedErrorState = { ...getCurrentFeesErrors };

                if (!updatedErrorState[category][index]) {
                    updatedErrorState[category][index] = getDefaultFeeRowErrorState();
                }

                if (isDelete) {
                    updatedErrorState[category][index] = {
                        ...getDefaultFeeRowErrorState(),
                        feeName: updatedErrorState[category][index].feeName,
                        isDeleted: true
                    };
                } else {
                    updatedErrorState[category][index] = {
                        ...updatedErrorState[category][index],
                        isDeleted: false
                    };
                }

                dispatchRedDotFeeError(category, updatedErrorState[category]);
            }
        },
        [setStatesByCategoryLookup, getCurrentFeesErrors]
    );

    const handleInputChange: FeesFormContextProps['handleInputChange'] = useCallback(
        ({ category, field, index, value }) => {
            const setStateFunc = setStatesByCategoryLookup[category];

            if (!setStateFunc) return;

            setStateFunc((prev) => {
                const feeToUpdate = prev[index];

                // Row was removed by a concurrent delete during rapid edits; the captured index is
                // now out of range, so drop this stale edit instead of dereferencing undefined.
                if (!feeToUpdate) return prev;

                if (feeToUpdate[field] !== value) {
                    const updatedFees = [...prev];
                    updatedFees[index] = { ...feeToUpdate, [field]: value, isEdited: true };
                    return updatedFees;
                }

                // istanbul ignore next
                return prev;
            });
        },
        [setStatesByCategoryLookup]
    );

    const handleInputBlur: FeesFormContextProps['handleInputBlur'] = useCallback(
        ({ category, field, index, isValid }) => {
            if (isDealScreenRedDotErrorsEnabled) {
                const fieldError = !isValid;

                const updatedErrorState = { ...getCurrentFeesErrors };

                if (!updatedErrorState[category][index]) {
                    updatedErrorState[category][index] = getDefaultFeeRowErrorState();
                }

                if (updatedErrorState[category][index][field] !== fieldError) {
                    updatedErrorState[category][index][field] = fieldError;
                    dispatchRedDotFeeError(category, updatedErrorState[category]);
                }
            }
        },
        [getCurrentFeesErrors]
    );

    const handleAddFee: FeesFormContextProps['handleAddFee'] = useCallback(
        ({ category, feeItems }) => {
            const setStateFunc = setStatesByCategoryLookup[category];
            setStateFunc([...feeItems, createNewFee(isLease, category)]);
            if (isDealScreenRedDotErrorsEnabled) {
                const updatedErrorState = { ...getCurrentFeesErrors };

                updatedErrorState[category].push({ ...getDefaultFeeRowErrorState(), feeTypeId: true });
                dispatchRedDotFeeError(category, updatedErrorState[category]);
            }
        },
        [isLease, setStatesByCategoryLookup, getCurrentFeesErrors]
    );

    const handleSaveClick = (payment = true) => {
        const updatedFees = [...govFees, ...dealerFees, ...lenderFees];
        const deletedFees = updatedFees.filter((fees) => fees.isDeleted && Number(fees.feeAmount) !== ELIMINATED_FEE_AMOUNT);
        const filteredUpdatedFees = updatedFees.filter((fees) => fees.isEdited || fees.isDeleted);
        const uniqueFilteredUpdatedFees = mergeFeeUpdates(filteredUpdatedFees);

        const newOverrides = uniqueFilteredUpdatedFees.map<FeeOverride>((fee) => {
            const effectiveCapIndicator = isLease && (fee.capIndicator ?? isAllFeesCapitalized);
            const baseOverride = {
                category: fee.category,
                feeDescription: fee.feeDescription,
                feeTypeId: Number(fee.feeTypeId),
                includeInCalcIndicator: fee.includedInCalc,
                reqCapIndicator: effectiveCapIndicator
            };

            if (isOptionalDeskingMode) {
                const feeAmount = fee.isDeleted ? ELIMINATED_FEE_AMOUNT : fee.feeAmount !== '' ? Number(fee.feeAmount) : undefined;
                const feeName = fee.feeName || fee.feeDescription || undefined;
                return { ...baseOverride, feeAmount, feeName } as FeeOverride;
            }

            return {
                ...baseOverride,
                feeAmount: fee.isDeleted ? ELIMINATED_FEE_AMOUNT : Number(fee.feeAmount),
                feeName: fee.feeName
            };
        });

        const overridesToSave = isOptionalDeskingMode
            ? [
                  ...(feesOverrides || []).filter(
                      (existing) =>
                          !newOverrides.some((n) => n.feeTypeId === existing.feeTypeId && n.category === existing.category) &&
                          !(
                              existing.feeAmount === ELIMINATED_FEE_AMOUNT &&
                              updatedFees.some(
                                  (fee) =>
                                      !fee.isDeleted &&
                                      Number(fee.feeTypeId) === existing.feeTypeId &&
                                      fee.category === existing.category
                              )
                          )
                  ),
                  ...newOverrides
              ]
            : newOverrides;

        onSave?.(overridesToSave, deletedFees, payment);

        if (isOptionalDeskingMode && !payment) {
            const isLocalOnlyFee = (propItems: CustomFeeItem[]) => (fee: CustomFeeItem) =>
                fee.feeTypeId === ADDED_FEE_CODE || !propItems.some((p) => p.feeTypeId === fee.feeTypeId);

            setGovFees([
                ...applyFeeOverrides(governmentFeesItems, overridesToSave),
                ...govFees.filter(isLocalOnlyFee(governmentFeesItems))
            ]);
            setDealerFees([
                ...applyFeeOverrides(dealerFeesItems, overridesToSave),
                ...dealerFees.filter(isLocalOnlyFee(dealerFeesItems))
            ]);
            setLenderFees([
                ...applyFeeOverrides(lenderFeesItems, overridesToSave),
                ...lenderFees.filter(isLocalOnlyFee(lenderFeesItems))
            ]);
        }
    };

    // commitFeeSoftSave: perform a soft save if allowed. Memoized so effects can depend on it.
    const commitFeeSoftSave = useCallback(() => {
        if (disableFeeSubmit || !hasFeesChanged || !areFeesValid) {
            return;
        }
        handleSaveClick();
    }, [disableFeeSubmit, hasFeesChanged, areFeesValid, handleSaveClick]);

    const commitFeeNameToRedux = useCallback(() => {
        if (isOptionalDeskingMode) {
            handleSaveClick(false);
        } else {
            commitFeeSoftSave();
        }
    }, [isOptionalDeskingMode, handleSaveClick, commitFeeSoftSave]);

    const [commitRequested, setCommitRequested] = useState(false);

    const commitFeeSoftSaveOnNextRender = useCallback(() => {
        shouldCommitFeeSoftSave.current = true;
        setCommitRequested(true);
    }, []);

    useEffect(() => {
        if (!commitRequested) return;

        // Reset request so future calls work the same way
        setCommitRequested(false);

        if (shouldCommitFeeSoftSave.current) {
            shouldCommitFeeSoftSave.current = false;
            commitFeeSoftSave();
        }
    }, [commitRequested, commitFeeSoftSave]);

    useEffect(() => {
        if (isLease && shouldCommitFeeSoftSave.current) {
            shouldCommitFeeSoftSave.current = false;
            commitFeeSoftSave();
        }
    }, [hasFeesChanged]);

    // Coalesce rapid fee changes (deletions/restores and upfront-checkbox toggles) into a single
    // payment (PO) call. Each change cancels the previously scheduled commit and reschedules, so only
    // the most up-to-date state (including every change made up to that point) is sent. This prevents
    // the flood of overlapping/racing PO requests that occurs when a dealer clicks quickly.
    const softSaveDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const commitFeeSoftSaveDebounced = useCallback(() => {
        if (softSaveDebounceTimerRef.current) {
            clearTimeout(softSaveDebounceTimerRef.current);
        }
        softSaveDebounceTimerRef.current = setTimeout(() => {
            softSaveDebounceTimerRef.current = null;
            commitFeeSoftSaveOnNextRender();
        }, SOFT_SAVE_DEBOUNCE_MS);
    }, [commitFeeSoftSaveOnNextRender]);

    // Keep a ref to the latest commitFeeSoftSave so the unmount cleanup can flush a
    // pending debounced change with up-to-date state. The cleanup effect runs with an
    // empty dependency array, so it would otherwise capture a stale commit closure.
    const commitFeeSoftSaveRef = useRef(commitFeeSoftSave);
    commitFeeSoftSaveRef.current = commitFeeSoftSave;

    useEffect(() => {
        return () => {
            if (softSaveDebounceTimerRef.current) {
                clearTimeout(softSaveDebounceTimerRef.current);
                softSaveDebounceTimerRef.current = null;
                // Flush the pending debounced change synchronously so the PO call is not
                // lost when the fees panel unmounts within the debounce window. commitFeeSoftSave
                // already guards on disableFeeSubmit/hasFeesChanged/areFeesValid, so this is a
                // no-op when there is nothing to commit.
                commitFeeSoftSaveRef.current();
            }
        };
    }, []);

    const handleCapitalizeAllFeesChange = useCallback(
        (isChecked: boolean) => {
            if (isChecked) {
                initialFeesRef.current = {
                    Government: govFees,
                    Dealer: dealerFees,
                    Lender: lenderFees
                };
            }

            Object.keys(setStatesByCategoryLookup).forEach((category) => {
                const typedCategory = category as keyof typeof setStatesByCategoryLookup;
                const setStateFunc = setStatesByCategoryLookup[typedCategory];

                if (!setStateFunc) return;

                setStateFunc((fees) =>
                    fees.map((fee) => {
                        if (fee.isDeleted) return fee;

                        const prevCapIndicator = initialFeesRef.current[typedCategory].find(
                            (x) => x.feeTypeId === fee.feeTypeId
                        )?.capIndicator;

                        const newCapIndicator = isChecked || Boolean(prevCapIndicator);

                        // Only mark as edited if the capIndicator value is actually changing
                        const hasCapIndicatorChanged = fee.capIndicator !== newCapIndicator;

                        return {
                            ...fee,
                            capIndicator: newCapIndicator,
                            // Preserve existing isEdited or set to true only if cap indicator changed
                            isEdited: fee.isEdited || hasCapIndicatorChanged
                        };
                    })
                );
            });
            commitFeeSoftSaveOnNextRender();
        },
        [govFees, dealerFees, lenderFees, setStatesByCategoryLookup, commitFeeSoftSaveOnNextRender]
    );

    useEffect(() => {
        if (!hasRunInitialCapRef.current) {
            hasRunInitialCapRef.current = true;
            return;
        }
        if (isCapAllFeeClickedManually) {
            handleCapitalizeAllFeesChange(isAllFeesCapitalized);
        }
    }, [isAllFeesCapitalized]);

    const handleConfirmDialogHide = () => {
        setShowConfirmDialog(false);
    };

    const handleConfirmDialogDone = () => {
        setShowConfirmDialog(false);

        dispatch(
            paymentActionCreators.updateUseCalculatedGovFees({
                useCalculatedGovFees: true,
                makePaymentCall: false
            })
        );
        onReset?.();
        setGovFees(governmentFeesItems);
        setDealerFees(dealerFeesItems);
        setLenderFees(lenderFeesItems);
        if (isDealScreenRedDotErrorsEnabled) {
            dispatch(dealScreenErrorActionCreators.clearEntityErrors(DealTabs.FEES));
        }
    };

    const handleConfigDialogCancel = () => {
        setShowConfirmDialog(false);
    };

    const handleResetClick = () => {
        setShowConfirmDialog(true);
    };

    const getIsIncludeInCalc: FeesFormContextProps['getIsIncludeInCalc'] = useCallback(({ category, includedInCalc }) => {
        return !includedInCalc && category === FeesCategoriesType.LENDER;
    }, []);

    const getCurrentlySelectedFees = useCallback(() => {
        return [...lenderFees, ...govFees, ...dealerFees];
    }, [lenderFees, govFees, dealerFees]);

    const handleSoftSaveOnEnterKey: FeesFormContextProps['handleSoftSaveOnEnterKey'] = (commitChanges) => (event) => {
        if (event.key === 'Enter') {
            commitChanges();
        }
    };

    const providerValue: FeesFormContextProps = useMemo(
        () => ({
            handleInputChange,
            handleInputBlur,
            handleDelete,
            getIsIncludeInCalc,
            handleAddFee,
            isLease,
            setDisableFeeSubmit,
            getCurrentlySelectedFees,
            commitFeeSoftSave: commitFeeSoftSaveOnNextRender,
            commitFeeSoftSaveDebounced,
            commitFeeNameToRedux,
            isOptionalDeskingMode,
            handleSoftSaveOnEnterKey,
            shouldTriggerValidation
        }),
        [
            handleInputChange,
            handleInputBlur,
            handleDelete,
            getIsIncludeInCalc,
            handleAddFee,
            isLease,
            getCurrentlySelectedFees,
            handleSaveClick,
            commitFeeSoftSave,
            commitFeeNameToRedux,
            isOptionalDeskingMode,
            handleSoftSaveOnEnterKey,
            commitFeeSoftSaveOnNextRender,
            commitFeeSoftSaveDebounced,
            shouldTriggerValidation
        ]
    );

    const renderFooter = () => {
        return (
            <>
                <Box display="flex" justifyContent="flex-end">
                    <TotalSummaryContainer BaseBreakPoint={BaseBreakPoint}>
                        <Typography variant="h5" sx={{ fontWeight: 400 }}>
                            {`Total ${FEES}`}
                        </Typography>

                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {formatDollarsAndCents(totalFees)}
                        </Typography>
                    </TotalSummaryContainer>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>{feesOverrides && feesOverrides.length > 0 && <ManualLegendMessage />}</Box>
                    <Box display="flex" justifyContent="flex-end" gap={3}>
                        <Button
                            data-testid="btn-deal-fees-edit-reset"
                            buttonStyle="tertiary"
                            type="reset"
                            size="small"
                            onClick={handleResetClick}
                            disabled={isLocked}
                        >
                            <ArrowUturnLeftIcon />
                            Reset All
                        </Button>
                    </Box>
                </Box>
            </>
        );
    };

    return (
        <Box>
            <Box>
                <FeesFormContext.Provider value={providerValue}>
                    <FeesFormCategoryItems
                        categoryTitle="Lender Fees"
                        feeItems={lenderFees}
                        categoryType="Lender"
                        dataTestId="form_category_lender_fees"
                    />
                    <FeesFormCategoryItems
                        categoryTitle="Government Fees"
                        feeItems={govFees}
                        categoryType="Government"
                        dataTestId="form_category_gov_fees"
                    />
                    <FeesFormCategoryItems
                        categoryType="Dealer"
                        categoryTitle="Dealer Fees"
                        feeItems={dealerFees}
                        dataTestId="form_category_dealer_fees"
                    />
                </FeesFormContext.Provider>
            </Box>
            {renderFooter()}
            {showConfirmDialog && (
                <FeeResetDialog
                    data-testid="reset-fees-confirm-dialog"
                    onHide={handleConfirmDialogHide}
                    onConfirm={handleConfirmDialogDone}
                    onCancel={handleConfigDialogCancel}
                />
            )}
        </Box>
    );
};

export default FeesForm;
